/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";

// Material Dashboard 2 React base styles
import colors from "assets/theme-dark/base/colors";
import breakpoints from "assets/theme-dark/base/breakpoints";
import typography from "assets/theme-dark/base/typography";
import boxShadows from "assets/theme-dark/base/boxShadows";
import borders from "assets/theme-dark/base/borders";
import globals from "assets/theme-dark/base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "assets/theme-dark/functions/boxShadow";
import hexToRgb from "assets/theme-dark/functions/hexToRgb";
import linearGradient from "assets/theme-dark/functions/linearGradient";
import pxToRem from "assets/theme-dark/functions/pxToRem";
import rgba from "assets/theme-dark/functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "assets/theme-dark/components/sidenav";
import list from "assets/theme-dark/components/list";
import listItem from "assets/theme-dark/components/list/listItem";
import listItemText from "assets/theme-dark/components/list/listItemText";
import card from "assets/theme-dark/components/card";
import cardMedia from "assets/theme-dark/components/card/cardMedia";
import cardContent from "assets/theme-dark/components/card/cardContent";
import button from "assets/theme-dark/components/button";
import iconButton from "assets/theme-dark/components/iconButton";
import input from "assets/theme-dark/components/form/input";
import inputLabel from "assets/theme-dark/components/form/inputLabel";
import inputOutlined from "assets/theme-dark/components/form/inputOutlined";
import textField from "assets/theme-dark/components/form/textField";
import menu from "assets/theme-dark/components/menu";
import menuItem from "assets/theme-dark/components/menu/menuItem";
import switchButton from "assets/theme-dark/components/form/switchButton";
import divider from "assets/theme-dark/components/divider";
import tableContainer from "assets/theme-dark/components/table/tableContainer";
import tableHead from "assets/theme-dark/components/table/tableHead";
import tableCell from "assets/theme-dark/components/table/tableCell";
import linearProgress from "assets/theme-dark/components/linearProgress";
import breadcrumbs from "assets/theme-dark/components/breadcrumbs";
import slider from "assets/theme-dark/components/slider";
import avatar from "assets/theme-dark/components/avatar";
import tooltip from "assets/theme-dark/components/tooltip";
import appBar from "assets/theme-dark/components/appBar";
import tabs from "assets/theme-dark/components/tabs";
import tab from "assets/theme-dark/components/tabs/tab";
import stepper from "assets/theme-dark/components/stepper";
import step from "assets/theme-dark/components/stepper/step";
import stepConnector from "assets/theme-dark/components/stepper/stepConnector";
import stepLabel from "assets/theme-dark/components/stepper/stepLabel";
import stepIcon from "assets/theme-dark/components/stepper/stepIcon";
import select from "assets/theme-dark/components/form/select";
import formControlLabel from "assets/theme-dark/components/form/formControlLabel";
import formLabel from "assets/theme-dark/components/form/formLabel";
import checkbox from "assets/theme-dark/components/form/checkbox";
import radio from "assets/theme-dark/components/form/radio";
import autocomplete from "assets/theme-dark/components/form/autocomplete";
import container from "assets/theme-dark/components/container";
import popover from "assets/theme-dark/components/popover";
import buttonBase from "assets/theme-dark/components/buttonBase";
import icon from "assets/theme-dark/components/icon";
import svgIcon from "assets/theme-dark/components/svgIcon";
import link from "assets/theme-dark/components/link";
import dialog from "assets/theme-dark/components/dialog";
import dialogTitle from "assets/theme-dark/components/dialog/dialogTitle";
import dialogContent from "assets/theme-dark/components/dialog/dialogContent";
import dialogContentText from "assets/theme-dark/components/dialog/dialogContentText";
import dialogActions from "assets/theme-dark/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            color: colors.text.main, // Màu chữ: #ffffffcc
            backgroundColor: colors.background.paper, // Màu nền: #202940
          },
          "& .MuiInputBase-root.Mui-disabled": {
            color: colors.text.disabled, // Màu chữ khi disabled: #ffffff66
            WebkitTextFillColor: colors.text.disabled, // Đảm bảo màu chữ hiển thị đúng trên Safari
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[300], // Màu viền mặc định: #424242
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[500], // Màu viền khi hover: #616161
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main, // Màu viền khi focused: #42a5f5
          },
          "& .MuiInputLabel-root": {
            color: colors.text.secondary, // Màu nhãn: #ffffff80
          },
          "& .MuiInputLabel-root.Mui-disabled": {
            color: colors.text.disabled, // Màu nhãn khi disabled: #ffffff66
            borderColor: colors.text.main,
          },
        },
      },
    },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
    MuiColorInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.card, // Nền tối: #202940
          border: `1px solid ${colors.grey[700]}`, // Viền: #495057
          borderRadius: "4px",
          color: colors.text.main, // Màu chữ: #ffffffcc
        },
        input: {
          color: colors.text.main, // Màu chữ trong input: #ffffffcc
          backgroundColor: colors.background.card, // Nền input: #202940
        },
        label: {
          color: colors.text.main, // Màu nhãn: #ffffffcc
        },
        colorButton: {
          backgroundColor: colors.grey[800], // Nền nút chọn màu: #343a40
          border: `1px solid ${colors.grey[700]}`, // Viền nút: #495057
          "&:hover": {
            backgroundColor: colors.grey[700], // Hover: #495057
          },
        },
        colorPreview: {
          border: `1px solid ${colors.grey[700]}`, // Viền ô màu: #495057
        },
      },
    },
    MuiDateTimePicker: {
      styleOverrides: {
        root: {
          width: "100%",
          textAlign: "right",
          "& .MuiInputBase-input": {
            padding: "10px 14px",
            color: colors.text.main, // Màu chữ trong input: #ffffffcc
            backgroundColor: colors.background.paper, // Màu nền: #202940
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[300], // Màu viền: #424242
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[500], // Màu viền khi hover: #616161
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main, // Màu viền khi focused: #42a5f5
          },
          // Tùy chỉnh lịch (calendar)
          "& .MuiPickersDay-root": {
            color: colors.text.main + " !important", // Màu chữ các ngày: #ffffffcc
            "&:hover": {
              backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
            },
          },
          "& .MuiPickersDay-dayOutsideMonth": {
            color: colors.text.disabled + " !important", // Màu chữ cho các ngày không thuộc tháng hiện tại: #ffffff66
          },
          "& .MuiPickersDay-today": {
            borderColor: colors.primary.main, // Viền ngày hiện tại: #42a5f5
          },
          "& .MuiPickersDay-root.Mui-selected": {
            backgroundColor: colors.primary.main, // Màu nền ngày được chọn: #42a5f5
            color: colors.white.main + " !important", // Màu chữ ngày được chọn: #ffffff
            "&:hover": {
              backgroundColor: colors.primary.focus, // Khi hover: #42a5f5
            },
          },
          // Tùy chỉnh năm
          "& .MuiPickersYear-root": {
            "& .MuiPickersYear-yearButton": {
              color: colors.text.main + " !important", // Màu chữ năm: #ffffffcc
              "&:hover": {
                backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
              },
            },
            "& .MuiPickersYear-yearButton.Mui-selected": {
              backgroundColor: colors.primary.main, // Màu nền năm được chọn: #42a5f5
              color: colors.white.main + " !important", // Màu chữ: #ffffff
              "&:hover": {
                backgroundColor: colors.primary.focus, // Khi hover: #42a5f5
              },
            },
          },
          // Tùy chỉnh tiêu đề năm/tháng (April 2025)
          "& .MuiPickersCalendarHeader-root": {
            "& .MuiPickersCalendarHeader-label": {
              color: colors.text.main + " !important", // Màu chữ "April 2025": #ffffffcc
            },
          },
          // Tùy chỉnh ngày trong tuần (S, M, T, W, T, F, S)
          "& .MuiDayPicker-weekDayLabel": {
            color: colors.text.main + " !important", // Màu chữ "S, M, T, W, T, F, S": #ffffffcc
          },
          // Tùy chỉnh đồng hồ (clock)
          "& .MuiPickersClockNumber-root": {
            color: colors.text.main + " !important", // Màu chữ trên đồng hồ: #ffffffcc
          },
          "& .MuiPickersClockNumber-root.Mui-selected": {
            backgroundColor: colors.primary.main, // Màu nền số được chọn: #42a5f5
            color: colors.white.main + " !important", // Màu chữ: #ffffff
          },
          "& .MuiPickersClockPointer-pointer": {
            backgroundColor: colors.primary.main, // Màu con trỏ: #42a5f5
          },
          "& .MuiPickersClockPointer-thumb": {
            borderColor: colors.primary.main, // Màu viền nút trên con trỏ: #42a5f5
            backgroundColor: colors.white.main, // Màu nền nút: #ffffff
          },
          // Tùy chỉnh mũi tên điều hướng
          "& .MuiPickersArrowSwitcher-button": {
            color: colors.text.main + " !important", // Màu mũi tên: #ffffffcc
            "&:hover": {
              backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
            },
          },
          // Tùy chỉnh toolbar (nếu có)
          "& .MuiPickersToolbar-root": {
            backgroundColor: colors.background.paper, // Màu nền toolbar: #202940
            color: colors.text.main + " !important", // Màu chữ: #ffffffcc
          },
          // Tùy chỉnh các nút hành động (OK, Cancel)
          "& .MuiDialogActions-root": {
            backgroundColor: colors.background.paper, // Màu nền: #202940
            "& .MuiButtonBase-root": {
              color: colors.text.main + " !important", // Màu chữ nút: #ffffffcc
              "&:hover": {
                backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
              },
            },
          },
          // Tùy chỉnh nền của popover
          "& .MuiPaper-root": {
            backgroundColor: colors.background.paper, // Màu nền popover: #202940
            color: colors.text.main + " !important", // Màu chữ: #ffffffcc
          },
        },
      },
    },
    MuiDatePicker: {
      styleOverrides: {
        root: {
          width: "100%",
          textAlign: "right",
          "& .MuiInputBase-input": {
            padding: "10px 14px",
            color: colors.text.main, // Màu chữ trong input: #ffffffcc
            backgroundColor: colors.background.paper, // Màu nền: #202940
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[300], // Màu viền: #424242
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey[500], // Màu viền khi hover: #616161
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main, // Màu viền khi focused: #42a5f5
          },
          // Tùy chỉnh lịch (calendar)
          "& .MuiPickersDay-root": {
            color: colors.text.main + " !important", // Màu chữ các ngày: #ffffffcc
            "&:hover": {
              backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
            },
          },
          "& .MuiPickersDay-dayOutsideMonth": {
            color: colors.text.disabled + " !important", // Màu chữ cho các ngày không thuộc tháng hiện tại: #ffffff66
          },
          "& .MuiPickersDay-today": {
            borderColor: colors.primary.main, // Viền ngày hiện tại: #42a5f5
          },
          "& .MuiPickersDay-root.Mui-selected": {
            backgroundColor: colors.primary.main, // Màu nền ngày được chọn: #42a5f5
            color: colors.white.main + " !important", // Màu chữ ngày được chọn: #ffffff
            "&:hover": {
              backgroundColor: colors.primary.focus, // Khi hover: #42a5f5
            },
          },
          // Tùy chỉnh năm
          "& .MuiPickersYear-root": {
            "& .MuiPickersYear-yearButton": {
              color: colors.text.main + " !important", // Màu chữ năm: #ffffffcc
              "&:hover": {
                backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
              },
            },
            "& .MuiPickersYear-yearButton.Mui-selected": {
              backgroundColor: colors.primary.main, // Màu nền năm được chọn: #42a5f5
              color: colors.white.main + " !important", // Màu chữ: #ffffff
              "&:hover": {
                backgroundColor: colors.primary.focus, // Khi hover: #42a5f5
              },
            },
          },
          // Tùy chỉnh tiêu đề năm/tháng (April 2025)
          "& .MuiPickersCalendarHeader-root": {
            "& .MuiPickersCalendarHeader-label": {
              color: colors.text.main + " !important", // Màu chữ "April 2025": #ffffffcc
            },
          },
          // Tùy chỉnh ngày trong tuần (S, M, T, W, T, F, S)
          "& .MuiDayPicker-weekDayLabel": {
            color: colors.text.main + " !important", // Màu chữ "S, M, T, W, T, F, S": #ffffffcc
          },
          // Tùy chỉnh mũi tên điều hướng
          "& .MuiPickersArrowSwitcher-button": {
            color: colors.text.main + " !important", // Màu mũi tên: #ffffffcc
            "&:hover": {
              backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
            },
          },
          // Tùy chỉnh toolbar (nếu có)
          "& .MuiPickersToolbar-root": {
            backgroundColor: colors.background.paper, // Màu nền toolbar: #202940
            color: colors.text.main + " !important", // Màu chữ: #ffffffcc
          },
          // Tùy chỉnh các nút hành động (OK, Cancel)
          "& .MuiDialogActions-root": {
            backgroundColor: colors.background.paper, // Màu nền: #202940
            "& .MuiButtonBase-root": {
              color: colors.text.main + " !important", // Màu chữ nút: #ffffffcc
              "&:hover": {
                backgroundColor: colors.grey[700], // Màu nền khi hover: #495057
              },
            },
          },
          // Tùy chỉnh nền của popover
          "& .MuiPaper-root": {
            backgroundColor: colors.background.paper, // Màu nền popover: #202940
            color: colors.text.main + " !important", // Màu chữ: #ffffffcc
          },
        },
      },
    },
  },
});
